import { Suspense, useEffect, useState } from 'react';
import { useSelection } from '../lib/selection-context';
import { useToast } from '../lib/toast-context';
import { useLayoutUser } from '../lib/layout-context';
import { formatBytes } from '../lib/format-bytes';
import {
  bulkDownloadHref,
  prepareBulkDownload,
  singleDownloadHref,
  triggerBrowserDownload,
} from '../lib/bulk-download';
import { BulkDeleteConfirm } from './BulkDeleteConfirm';
import { ButtonInverse } from './ui/ButtonInverse';
import { ActionMenu } from './ui/ActionMenu';
import { I } from '../lib/icons';
import { useContextualTip } from '../lib/use-contextual-tip';
import type { AssetListItem } from '../lib/asset-types';
import { lazyWithRetry } from '../lib/lazyWithRetry';

// Dialoge erst beim Öffnen laden — sie lagen sonst im DAM-Entry-Chunk
// (`bundle:check`, scripts/check-entry-bundle-size.mjs). Chunk-Fehler fängt die
// route-weite LazyChunkErrorBoundary (App.tsx).
const BulkTagModal = lazyWithRetry(() =>
  import('./BulkTagModal').then((m) => ({ default: m.BulkTagModal })),
);
const BulkFacetModal = lazyWithRetry(() =>
  import('./BulkFacetModal').then((m) => ({ default: m.BulkFacetModal })),
);
const BulkRecipeModal = lazyWithRetry(() =>
  import('./BulkRecipeModal').then((m) => ({ default: m.BulkRecipeModal })),
);
const BulkShareModal = lazyWithRetry(() =>
  import('./BulkShareModal').then((m) => ({ default: m.BulkShareModal })),
);
const RenameModal = lazyWithRetry(() =>
  import('./RenameModal').then((m) => ({ default: m.RenameModal })),
);

type Props = {
  /** Callback nach erfolgreicher Bulk-Op — Library refetcht + clear ist Caller-Sache. */
  onAfterAction: () => void;
  /** Sichtbare Asset-Liste (Sortier-Reihenfolge) — für die Rename-Vorschau +
   *  sequence-Nummerierung. Wird auf die Selektion gefiltert, Order bleibt erhalten. */
  assets: AssetListItem[];
};

type ActiveDialog =
  | 'tags'
  | 'tags-remove'
  | 'facets'
  | 'facets-remove'
  | 'recipe'
  | 'share'
  | 'rename'
  | 'delete'
  | null;

export function BulkActionBar({ onAfterAction, assets }: Props) {
  const selection = useSelection();
  const toast = useToast();
  // Onboarding: einmaliger Tipp beim ersten Erscheinen der Bulk-Bar (erste Selektion).
  useContextualTip('bulk-bar', selection.count > 0, {
    anchor: 'bulk-bar',
    title: 'Mehrere auf einmal',
    description:
      'Sobald du Assets auswählst, erscheint diese Leiste — herunterladen, teilen, taggen oder löschen in einem Rutsch.',
    side: 'top',
  });
  // Delete ist Backend-Admin-only — Button für Member ausblenden (sonst 403).
  const isAdmin = useLayoutUser()?.role === 'Admin';
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  // Debounced count for aria-live — verhindert Spam bei shift-click-range-select
  const [announcedCount, setAnnouncedCount] = useState(selection.count);

  useEffect(() => {
    const t = window.setTimeout(() => setAnnouncedCount(selection.count), 200);
    return () => window.clearTimeout(t);
  }, [selection.count]);

  // Wenn Selection auf 0 fällt → offenen Dialog reset (sonst würde er beim
  // nächsten count>0 sofort wieder aufpoppen). "Adjust state during render"
  // statt useEffect+setState (siehe patterns/react.md). React erkennt
  // setState während Render und re-rendered ohne intermediate paint.
  const [prevCount, setPrevCount] = useState(selection.count);
  if (prevCount !== selection.count) {
    setPrevCount(selection.count);
    if (selection.count === 0 && activeDialog !== null) {
      setActiveDialog(null);
    }
  }

  if (selection.count === 0) {
    return (
      <div
        role="region"
        aria-label="Bulk-Aktionen"
        aria-live="polite"
        className="sr-only"
      >
        Keine Assets ausgewählt
      </div>
    );
  }

  const ids = Array.from(selection.selectedIds);
  // Selektierte Assets in SICHTBARER Sortier-Reihenfolge (assets ist sortiert) —
  // für Rename-Vorschau + sequence-Nummerierung. filter() bewahrt die Order.
  const selectedAssets = assets
    .filter((a) => selection.selectedIds.has(a.id))
    .map((a) => ({
      id: a.id,
      displayFilename: a.displayFilename,
      thumbUrl: a.thumbUrl,
      assetType: a.assetType,
    }));

  async function handleDownload(): Promise<void> {
    // 1 Asset → direkter Single-Download (kein ZIP-Overhead). >1 → ein ZIP über
    // den prepare→stream-Pfad. Selektion NICHT clearen (Download ist non-destruktiv).
    if (ids.length === 1) {
      triggerBrowserDownload(singleDownloadHref(ids[0]));
      return;
    }

    const outcome = await prepareBulkDownload(ids);
    if (!outcome.ok) {
      const e = outcome.error;
      if (e.kind === 'too_large') {
        toast.error(
          `Auswahl zu groß — max ${e.limitAssets} Assets / ${formatBytes(e.limitBytes)}.`,
        );
      } else if (e.kind === 'empty') {
        toast.error('Keine herunterladbaren Assets in der Auswahl.');
      } else if (e.kind === 'busy') {
        toast.error('Zu viele parallele Downloads aktiv — bitte kurz warten und erneut versuchen.');
      } else {
        toast.error('Download fehlgeschlagen. Bitte erneut versuchen.');
      }
      return;
    }

    triggerBrowserDownload(bulkDownloadHref(outcome.data.token));
    const { count, skipped } = outcome.data;
    if (skipped > 0) {
      toast.info(
        `${count} von ${count + skipped} Assets werden als ZIP heruntergeladen (${skipped} nicht verfügbar).`,
      );
    } else {
      toast.info(`${count} Assets werden als ZIP heruntergeladen.`);
    }
  }

  return (
    <>
      <div
        role="region"
        aria-label="Bulk-Aktionen"
        data-tour="bulk-bar"
        className="fixed bottom-4 inset-x-4 md:inset-x-auto md:bottom-6 md:left-1/2 md:-translate-x-1/2 z-40 flex items-center justify-center gap-2 px-4 py-3 bg-ink-900 text-white rounded-2xl shadow-pop flex-wrap md:flex-nowrap md:max-w-[calc(100vw-2rem)]"
      >
        {/* Redesign S2 (2026-10-10): dunkle Fläche ink-900 statt Grün; sichtbar
            nur die häufigen Aktionen Herunterladen · Teilen · Taggen, der Rest
            im ⋯-Menü (öffnet nach oben). „Zur Collection" aus dem Handoff hat
            keine Bulk-Grundlage (kein Endpunkt) → nicht gebaut. */}
        {/* Mobile: volle Breite (inset-x-4), Zähler eigene Zeile, Aktionen darunter. */}
        <span aria-live="polite" className="w-full md:w-auto text-center md:text-left text-sm font-semibold md:pr-2 whitespace-nowrap">
          {announcedCount} {announcedCount === 1 ? 'Asset' : 'Assets'} ausgewählt
        </span>
        <div className="hidden md:block w-px h-6 bg-white/20" aria-hidden="true" />
        <ButtonInverse variant="secondary" onClick={() => void handleDownload()}>
          <I.download size={14} aria-hidden />
          Herunterladen
        </ButtonInverse>
        <ButtonInverse variant="secondary" onClick={() => setActiveDialog('share')}>
          <I.link size={14} aria-hidden />
          Teilen
        </ButtonInverse>
        <ButtonInverse variant="secondary" onClick={() => setActiveDialog('tags')}>
          <I.tag size={14} aria-hidden />
          Taggen
        </ButtonInverse>
        <ActionMenu
          inverse
          side="top"
          label={<I.more size={16} aria-hidden />}
          ariaLabel="Weitere Aktionen"
          items={[
            { label: 'Tags entfernen', onSelect: () => setActiveDialog('tags-remove') },
            { label: 'Facetten zuweisen', onSelect: () => setActiveDialog('facets') },
            { label: 'Facetten entfernen', onSelect: () => setActiveDialog('facets-remove') },
            { label: 'Zu Rezept', onSelect: () => setActiveDialog('recipe') },
            { label: 'Umbenennen', onSelect: () => setActiveDialog('rename') },
            ...(isAdmin
              ? [{ label: 'Löschen', onSelect: () => setActiveDialog('delete'), danger: true }]
              : []),
          ]}
        />
        <div className="hidden md:block w-px h-6 bg-white/20" aria-hidden="true" />
        <button
          type="button"
          onClick={() => selection.clear()}
          aria-label="Auswahl aufheben"
          title="Auswahl aufheben"
          className="w-9 h-9 flex items-center justify-center rounded-control text-white/80 hover:text-white hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <I.close size={16} />
        </button>
      </div>

      {/* Eigene Suspense-Grenze: ohne sie suspendiert der Lazy-Dialog bis zur
          Route-Suspense hoch und ersetzt die ganze Seite durch den Spinner. */}
      <Suspense fallback={null}>
      {(activeDialog === 'tags' || activeDialog === 'tags-remove') && (
        <BulkTagModal
          assetIds={ids}
          mode={activeDialog === 'tags-remove' ? 'remove' : 'add'}
          onClose={() => setActiveDialog(null)}
          onAfterAction={onAfterAction}
          onSuccess={() => {
            setActiveDialog(null);
            selection.clear();
            onAfterAction();
          }}
        />
      )}
      {(activeDialog === 'facets' || activeDialog === 'facets-remove') && (
        <BulkFacetModal
          assetIds={ids}
          mode={activeDialog === 'facets-remove' ? 'remove' : 'add'}
          onClose={() => setActiveDialog(null)}
          onAfterAction={onAfterAction}
          onSuccess={() => {
            setActiveDialog(null);
            selection.clear();
            onAfterAction();
          }}
        />
      )}
      {activeDialog === 'recipe' && (
        <BulkRecipeModal
          assetIds={ids}
          onClose={() => setActiveDialog(null)}
          onSuccess={() => {
            // Library-Grid ändert sich durch Rezept-Zuordnung nicht → kein
            // onAfterAction-Refetch nötig, nur Selektion aufheben + schließen.
            setActiveDialog(null);
            selection.clear();
          }}
        />
      )}
      {activeDialog === 'share' && (
        <BulkShareModal
          assetIds={ids}
          previewAssets={selectedAssets}
          onCancel={() => setActiveDialog(null)}
          onDone={() => {
            setActiveDialog(null);
            selection.clear();
          }}
        />
      )}
      {activeDialog === 'rename' && (
        <RenameModal
          assets={selectedAssets}
          onClose={() => setActiveDialog(null)}
          onSuccess={() => {
            setActiveDialog(null);
            selection.clear();
            onAfterAction();
          }}
        />
      )}
      </Suspense>
      {activeDialog === 'delete' && (
        <BulkDeleteConfirm
          assetIds={ids}
          previewAssets={selectedAssets}
          onClose={() => setActiveDialog(null)}
          onSuccess={() => {
            setActiveDialog(null);
            selection.clear();
            onAfterAction();
          }}
        />
      )}
    </>
  );
}
